import type { TrackPurchaseRequest } from '../../search/models/models';
import type {
    ApiEventEntity,
    ApiEventsRequest,
    ApiTrackEvent,
} from '../../tracking/api/trackingRequests';
import type {
    ClickTrackingData,
    ProductTrackingData,
    RecipeClickTrackingData,
} from '../models/domain/personalizedTracking';

import { Browser } from '../../../utility/browser';
import trackingDirectApi from '../../tracking/api/trackingApi';
import { failSilently } from '../tracking/utils/failSilently';

const recipeClick = (clickEvent: RecipeClickTrackingData) => {
    const eventEntity: ApiEventEntity = {
        id: clickEvent.recipeId,
        type: 'RecipesEntity',
    };

    const eventsRequest: ApiEventsRequest = {
        events: [
            {
                type: 'click',
                entity: eventEntity,
            },
        ],
    };

    const device = Browser.platformType();
    failSilently(() => {
        trackingDirectApi.track(eventsRequest, device);
    })();
};

/** Use to track click event to personalization api (will go to loop54) */
const click = (clickEvent: ClickTrackingData) => {
    const eventEntity: ApiEventEntity = {
        id: clickEvent.productId,
        type: 'Product',
    };

    const eventsRequest: ApiEventsRequest = {
        events: [
            {
                type: 'click',
                entity: eventEntity,
            },
        ],
    };
    const device = Browser.platformType();
    failSilently(() => {
        trackingDirectApi.track(eventsRequest, device);
    })();
};

const addToCart = (productData: ProductTrackingData[]) => {
    const events = productData.map((data) => {
        const { price, quantity } = calculateTrackingData(data);
        const event: ApiTrackEvent = {
            type: 'addtocart',
            entity: {
                id: data.identifier,
                type: 'Product',
            },
            quantity,
            revenue: price,
        };
        return event;
    });
    defaultTrackEvent(...events);
};

const removeFromCart = (productData: ProductTrackingData) => {
    const { price, quantity } = calculateTrackingData(productData);

    const event: ApiTrackEvent = {
        type: 'removefromcart',
        entity: {
            id: productData.identifier,
            type: 'Product',
        },
        quantity,
        revenue: price,
    };
    defaultTrackEvent(event);
};

const purchase = (purchaseEvent: TrackPurchaseRequest) => {
    const event: ApiTrackEvent = {
        type: 'purchase',
        orderId: purchaseEvent.orderId,
        revenue: purchaseEvent.revenue,
        cart: purchaseEvent.products.map((e) => ({
            productId: e.productId,
            quantity: e.quantity,
            itemPrice: e.itemPrice,
        })),
    };

    defaultTrackEvent(event);
};

const calculateTrackingData = (productData: ProductTrackingData) => {
    let { quantity } = productData;
    if (productData.previousQuantity) {
        quantity = Math.abs(productData.quantity - productData.previousQuantity);
    }

    const trackedPrice = productData.promotionPrice
        ? productData.promotionPrice
        : productData.price || 0;
    const price = trackedPrice && (quantity * (trackedPrice * 100)) / 100;

    return {
        price,
        quantity,
    };
};

const defaultTrackEvent = (...events: ApiTrackEvent[]) => {
    const eventsRequest: ApiEventsRequest = {
        events,
    };

    const device = Browser.platformType();
    failSilently(() => {
        trackingDirectApi.track(eventsRequest, device);
    })();
};

const trackingFlow = {
    click,
    addToCart,
    purchase,
    recipeClick,
    removeFromCart,
};

export default trackingFlow;
